import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Mail, Phone, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps } from '@/types/chatbot'

const ctaIcons: Record<string, typeof Mail> = {
  email: Mail,
  phone: Phone,
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const isUser = message.role === 'user'

  const handleCopy = async (id: string, value: string) => {
    await navigator.clipboard.writeText(value)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-2.5',
          isUser
            ? 'bg-gray-900 text-white rounded-br-sm'
            : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'
        )}
      >
        {isTyping ? (
          <div className="flex gap-1.5 py-1 px-1">
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {message.content}
          </p>
        )}
      </div>

      {/* Contact CTAs - only for assistant messages, not typing */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 ml-1">
          {contactCTAs.map((cta) => {
            const Icon = ctaIcons[cta.id]
            const isCopied = copiedId === cta.id

            return (
              <button
                key={cta.id}
                type="button"
                onClick={() => handleCopy(cta.id, cta.value)}
                className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
              >
                {isCopied ? (
                  <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-600" />
                ) : (
                  Icon && <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                )}
                <span className={isCopied ? 'text-green-600' : ''}>
                  {isCopied ? t('chatbot.copied') : t(cta.labelKey)}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
